/**
 * .js-appear 内の要素内で 表示するコンテンツを切り替える
 *
 * <html例>
 * <div class="js-appear">
 *   <div id="content1">
 *     <button class="js-appear-toggle" data-target="content2">次へ</button>
 *   </div>
 *   <div id="content2 is_hidden">
 *     <button class="js-appear-toggle" data-target="content1">戻る</button>
 *     <button class="js-appear-toggle" data-target="content3">次へ</button>
 *   </div>
 *   <div id="content3 is_hidden">
 *     <button class="js-appear-toggle" data-target="content2">戻る</button>
 *     <button class="js-appear-toggle" data-target="content1">最初へ</button>
 *   </div>
 * </div>
 *
 * <動作>
 * - .js-appear-toggleをクリックすると、同じ.js-appear内の指定要素だけを表示
 * - data-targetで表示する要素のIDを指定
 * - .is_hiddenクラスで表示/非表示を制御
 * - 初期表示は最初に表示されている要素、または最初の子要素を表示
 */
export const initAppearToggles = () => {
  // クリックで表示切替
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".js-appear-toggle");
    if (!btn) return;

    const targetId = btn.getAttribute("data-target");
    if (!targetId) return;

    const container = btn.closest(".js-appear");
    if (!container) return;

    const children = Array.from(container.children);
    children.forEach((child) => {
      child.classList.add("is_hidden");
    });

    const targetEl = children.find((child) => child.id === targetId);
    if (targetEl) {
      targetEl.classList.remove("is_hidden");
    }

    const isActiveElements = document.querySelectorAll(
      `[data-appear-name=${targetId}]`
    );

    document.querySelectorAll(`[data-appear-name]`).forEach((element) => {
      element.classList.remove("is_active");
    });

    isActiveElements.forEach((element) => {
      element.classList.add("is_active");
    });
  });

  // 初期化：非表示の揺れを防ぐ
  document.querySelectorAll(".js-appear").forEach((container) => {
    const children = Array.from(container.children);
    if (children.length === 0) return;

    const firstVisible = children.find(
      (c) => !c.classList.contains("is_hidden")
    );
    if (firstVisible) {
      children.forEach((c) => {
        if (c !== firstVisible) c.classList.add("is_hidden");
      });
    } else {
      children.forEach((c, idx) => {
        c.classList.toggle("is_hidden", idx !== 0);
      });
    }
  });
};

/**
 * フォームフィールドの値を指定された要素にリアルタイム反映する
 *
 * 指定されたルート要素内の入力フィールド（input, textarea, select）の値を監視し、
 * 対応するdata-form属性を持つ要素にリアルタイムで反映する。
 *
 * @param {HTMLElement | Document} root - 検索を開始する親要素。デフォルトはdocument
 *
 * @example
 * // HTML例
 * // <input name="username" value="山田太郎">
 * // <div data-form="username"></div>
 * // ↓ 実行結果
 * // <div data-form="username">山田太郎</div>
 *
 */
export const bindLiveFormMirrors = (root = document) => {
  const fields = root.querySelectorAll(
    "input[name], textarea[name], select[name]"
  );
  fields.forEach((field) => {
    const name = field.getAttribute("name");
    if (!name) return;

    const render = () => {
      const targets = root.querySelectorAll(`[data-form="${name}"]`);
      const value =
        field.tagName === "SELECT"
          ? field.selectedIndex >= 0
            ? field.options[field.selectedIndex].text
            : ""
          : field.value;

      targets.forEach((n) => {
        n.textContent = value;
      });
    };

    render();
    field.addEventListener("input", render);
    field.addEventListener("change", render);
  });
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * コンタクトフォーム（.js-form-confirm）の一括バリデーション
 * エラー時はモーダルを開かず、各 data-error-for にメッセージを表示する。
 */
export const initFormValidation = () => {
  const clearFieldError = (form, fieldKey) => {
    const errEl = form.querySelector(`[data-error-for="${fieldKey}"]`);
    let control =
      fieldKey === "privacy-policy-02"
        ? form.querySelector("#privacy-policy-02")
        : form.querySelector(`[name="${fieldKey}"]`);

    if (errEl) {
      errEl.textContent = "";
      errEl.classList.remove("is-visible");
    }
    if (control) {
      control.classList.remove("is-invalid");
      control.removeAttribute("aria-invalid");
    }
    const row = form.querySelector(".form-main__row--privacy");
    if (fieldKey === "privacy-policy-02" && row) {
      row.classList.remove("is-invalid");
    }
  };

  const showFieldError = (form, fieldKey, message) => {
    const errEl = form.querySelector(`[data-error-for="${fieldKey}"]`);
    let control =
      fieldKey === "privacy-policy-02"
        ? form.querySelector("#privacy-policy-02")
        : form.querySelector(`[name="${fieldKey}"]`);

    if (errEl) {
      errEl.textContent = message;
      errEl.classList.add("is-visible");
    }
    if (control) {
      control.setAttribute("aria-invalid", "true");
      if (errEl?.id) {
        control.setAttribute("aria-describedby", errEl.id);
      }
      control.classList.add("is-invalid");
    }
    const row = form.querySelector(".form-main__row--privacy");
    if (fieldKey === "privacy-policy-02" && row) {
      row.classList.add("is-invalid");
    }
  };

  const validateForm02 = (form) => {
    const errors = [];

    const nameEl = form.querySelector('[name="name-02"]');
    const mailEl = form.querySelector('[name="mail-02"]');
    const msgEl = form.querySelector('[name="message-02"]');
    const privacyEl = form.querySelector("#privacy-policy-02");

    if (!nameEl?.value.trim()) {
      errors.push({
        key: "name-02",
        message: "お名前を入力してください",
        focusEl: nameEl,
      });
    }

    const mailVal = mailEl?.value.trim() ?? "";
    if (!mailVal) {
      errors.push({
        key: "mail-02",
        message: "メールアドレスを入力してください",
        focusEl: mailEl,
      });
    } else if (!EMAIL_PATTERN.test(mailVal)) {
      errors.push({
        key: "mail-02",
        message: "正しいメールアドレスを入力してください",
        focusEl: mailEl,
      });
    }

    if (!msgEl?.value.trim()) {
      errors.push({
        key: "message-02",
        message: "メッセージ本文を入力してください",
        focusEl: msgEl,
      });
    }

    if (privacyEl && !privacyEl.checked) {
      errors.push({
        key: "privacy-policy-02",
        message: "同意のチェックを入れてください",
        focusEl: privacyEl,
      });
    }

    return errors;
  };

  document.querySelectorAll("#form-02").forEach((form) => {
    ["name-02", "mail-02", "message-02"].forEach((name) => {
      const el = form.querySelector(`[name="${name}"]`);
      if (el) {
        el.addEventListener("input", () => clearFieldError(form, name));
      }
    });
    const privacy = form.querySelector("#privacy-policy-02");
    if (privacy) {
      privacy.addEventListener("change", () =>
        clearFieldError(form, "privacy-policy-02")
      );
    }
  });

  document.querySelectorAll(".js-form-confirm").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      const form = btn.closest("form");
      if (!form || form.id !== "form-02") return;

      const modalId = btn.getAttribute("data-modal-content");
      const modal = modalId ? document.getElementById(modalId) : null;

      ["name-02", "mail-02", "message-02", "privacy-policy-02"].forEach((key) =>
        clearFieldError(form, key)
      );

      const errors = validateForm02(form);
      if (errors.length > 0) {
        errors.forEach(({ key, message }) => {
          showFieldError(form, key, message);
        });
        const first = errors[0];
        if (first.focusEl) {
          first.focusEl.focus({ preventScroll: true });
          first.focusEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }

      if (modal) {
        document.body.classList.add("no-scroll");
        modal.classList.add("is-open");
      }
    });
  });

  const bindModalCloseWithoutTrigger = (modalId) => {
    const modal = document.getElementById(modalId);
    if (!modal || modal.dataset.closeBound === "1") return;
    modal.dataset.closeBound = "1";
    const body = document.querySelector("body");
    const close = () => {
      modal.classList.remove("is-open");
      body?.classList.remove("no-scroll");
    };
    modal.querySelectorAll(".modal-close").forEach((btn) => {
      btn.addEventListener("click", close);
    });
    modal.querySelector(".modal-bg")?.addEventListener("click", close);
  };

  bindModalCloseWithoutTrigger("contact-modal-02");
};
